import { Provider } from '@angular/core'

import { MarkedExtension, Tokens } from 'marked'
import { MARKED_EXTENSIONS } from 'ngx-markdown'

import { FeatureFlagsService } from '../feature-flags/feature-flags.service'

// Math delimiters, mirroring the KaTeX auto-render delimiters configured in
// BotResponseComponent: $$…$$ and \[…\] (display), $…$ and \(…\) (inline).
//
// The $/$$ rule is the standard one from marked-katex-extension: the closing
// delimiter must be followed by whitespace/punctuation/end, which keeps prose
// like "$5 and $10" from being captured as math. Content is single-line.
const DOLLAR_INLINE =
  /^(\${1,2})(?!\$)((?:\\.|[^\\\n])*?(?:\\.|[^\\\n$]))\1(?=[\s?!.,:？！。，：]|$)/
const PAREN_INLINE = /^\\\(([\s\S]+?)\\\)/
const BRACKET_INLINE = /^\\\[([\s\S]+?)\\\]/
const DOLLAR_BLOCK = /^\${2}\n([\s\S]+?)\n\${2}(?:\n|$)/
const BRACKET_BLOCK = /^\\\[\n([\s\S]+?)\n\\\](?:\n|$)/
const BLOCK_START = /(^|\n)(\${2}\n|\\\[\n)/

// Keep the markup valid without touching the LaTeX: KaTeX auto-render reads
// textContent, where these entities decode back to the original characters.
const escapeHtml = (raw: string): string =>
  raw.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

/**
 * A `marked` extension that shields LaTeX math from markdown processing.
 *
 * ngx-markdown renders markdown with `marked` first, then KaTeX auto-render
 * scans the result for `$…$` etc. Without this, `marked`'s inline rules corrupt
 * the LaTeX before KaTeX ever sees it — backslash-escaped punctuation is
 * unescaped (`\%` → `%`, which breaks `\text{\%}`), `*`/`_` turn into emphasis,
 * and so on — so KaTeX throws and renders the broken source in red.
 *
 * This extension tokenises math spans up-front and re-emits them verbatim, so
 * `marked` never touches their contents; the existing `[katex]` auto-render
 * then renders the intact LaTeX after sanitisation, exactly as before.
 *
 * Gated on `free-chat.katex` so behaviour is unchanged where KaTeX is off:
 * math is then left to ordinary markdown processing, as it was previously.
 */
const buildMathExtension = (enabled: () => boolean): MarkedExtension => {
  const emit = (token: Tokens.Generic): string => escapeHtml(token.raw)

  return {
    extensions: [
      {
        name: 'katexMathBlock',
        level: 'block',
        start(src: string) {
          if (!enabled()) return undefined
          const match = BLOCK_START.exec(src)
          return match ? match.index + match[1].length : undefined
        },
        tokenizer(src: string) {
          if (!enabled()) return undefined
          const match = DOLLAR_BLOCK.exec(src) ?? BRACKET_BLOCK.exec(src)
          return match ? { type: 'katexMathBlock', raw: match[0] } : undefined
        },
        renderer: emit
      },
      {
        name: 'katexMathInline',
        level: 'inline',
        start(src: string) {
          if (!enabled()) return undefined
          const candidates = [src.indexOf('$'), src.indexOf('\\('), src.indexOf('\\[')].filter(
            (index) => index >= 0
          )
          return candidates.length ? Math.min(...candidates) : undefined
        },
        tokenizer(src: string) {
          if (!enabled()) return undefined
          const match =
            DOLLAR_INLINE.exec(src) ?? PAREN_INLINE.exec(src) ?? BRACKET_INLINE.exec(src)
          return match ? { type: 'katexMathInline', raw: match[0] } : undefined
        },
        renderer: emit
      }
    ]
  }
}

/**
 * Registers the math-shielding `marked` extension with ngx-markdown. Add to an
 * app's providers alongside `provideMarkdown()`.
 */
export const provideKatexMathExtension = (): Provider => ({
  provide: MARKED_EXTENSIONS,
  multi: true,
  useFactory: (flags: FeatureFlagsService): MarkedExtension =>
    buildMathExtension(() => flags.has('free-chat.katex')),
  deps: [FeatureFlagsService]
})
